<template>
  <div v-if="items.length">
    <div class="results-nb-and-filters">
      <strong class="results-nb">{{
        translationsStore.t("results_nb", { count: items.length, type: type })
      }}</strong>
      <!-- SELECT SPA / STEP 1 PARCOURS SOINS -->
      <div
        v-if="journeyType === 'service' && centers?.length > 1"
        class="select-spa"
      >
        <n-select
          :key="bookingStore.currentStep"
          v-model:value="selectedCenter"
          size="large"
          :options="centersName"
          :placeholder="placeholderText"
          label-field="label"
          value-field="value"
          :show-checkmark="false"
          @update:value="selectCenter"
        />
      </div>
    </div>
    <!-- FILTERS STEP 2 -->
    <div
      v-if="journeyType === 'center' && type === 'service'"
      class="filters-wrapper"
    >
      <button
        v-if="showScrollButtons && !isAtStart"
        class="scroll-button start"
        @click="scrollToStart"
      >
        Scroll to start
      </button>
      <div
        ref="filtersContainer"
        class="filters"
        @scroll="checkScrollPosition"
      >
        <!-- Iconic Filter Button -->
        <button
          :class="{ active: iconicOnly }"
          @click="toggleIconicFilter"
        >
          {{ "Nos iconiques" }} ({{ iconicCount }})
        </button>

        <!-- Treatment Family Filter Buttons -->
        <button
          v-for="family in availableFamilies"
          :key="family"
          :class="{ active: selectedFamily === family }"
          @click="toggleFamilyFilter(family)"
        >
          {{ family }} ({{ filtersCounts[family] || 0 }})
        </button>
        <!-- <button
            :class="{ active: selectedFamily === '' }"
            @click="setFamilyFilter('')"
        >
            Show all
        </button> -->
      </div>
      <button
        v-if="showScrollButtons"
        :disabled="isAtEnd"
        class="scroll-button end"
        @click="scrollToEnd"
      >
        Scroll to end
      </button>
    </div>
    <BookingCard
      v-for="item in centerFilteredItems"
      :key="item.id"
      :item="item"
      :type="type"
      :selected-center="centers"
      @select-item="handleSelectItem"
    />
  </div>
</template>

<script setup>
import {
  watch,
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  nextTick,
} from "vue";

import BookingCard from "./molecules/BookingCard.vue";

import { useTranslationsStore } from "../stores/translations";
import { useBookingStore } from "../stores/booking";

const props = defineProps({
  items: {
    type: Array,
    default: () => [],
  },
  type: {
    type: String,
    required: true,
  },
  centers: {
    type: Array,
    required: false,
    default: () => [],
  },
});

const emit = defineEmits(["selectItem", "selectCenter"]);

const translationsStore = useTranslationsStore();
const bookingStore = useBookingStore();

// General State
const localItems = ref([...props.items]);
const selectedCenter = ref(null);
const hasSelectedDefaultCenter = ref(false);

// Filter State
const iconicOnly = ref(false);
const selectedFamily = ref("");
// Set iconic filter by default if treatment list
if (bookingStore.journeyType === "center" && props.type === "service") {
  iconicOnly.value = true;
}

// Scroll State
const filtersContainer = ref(null);
const showScrollButtons = ref(false);
const isAtStart = ref(true);
const isAtEnd = ref(false);

// Compute numbers of results corresponding to filter
const iconicCount = computed(() => {
  return props.items.filter((item) => item.iconic).length;
});
const filtersCounts = computed(() => {
  const counts = {};

  props.items.forEach((item) => {
    const raw = item.serviceFamily;

    const families = Array.isArray(raw)
      ? raw
      : raw && typeof raw === "object"
        ? [raw]
        : typeof raw === "string" && raw.trim() !== ""
          ? [raw]
          : [];

    families.forEach((family) => {
      if (!counts[family]) {
        counts[family] = 0;
      }
      counts[family]++;
    });
  });

  return counts;
});

//Computes placeholder select spa / parcours Soins
const placeholderText = computed(() => {
  const centers = props.centers;

  if (bookingStore.currentStep > 1 || props.type !== "service") {
    return "";
  }

  if (centers.length === 1) {
    return centers[0].title;
  } else if (centers.length > 1) {
    const sortedCenters = [...centers].sort((a, b) =>
      a.ville.localeCompare(b.ville)
    );
    const cityNames = sortedCenters
      .filter((center) => center.title)
      .map((center) => center.ville)
      .join(", ");
    return `${translationsStore.t("spa_select_placeholder")} ${cityNames}`;
  } else {
    return "";
  }
});

// Extract available treatment families from items
const availableFamilies = computed(() => {
  if (props.items.length > 0 && props.type === "service") {
    const familiesSet = new Set();

    props.items.forEach((item) => {
      const raw = item.serviceFamily;

      // Normalize to array
      const families = Array.isArray(raw)
        ? raw
        : raw && typeof raw === "object"
          ? [raw]
          : typeof raw === "string" && raw.trim() !== ""
            ? [raw]
            : [];

      families.forEach((family) => familiesSet.add(family));
    });

    return Array.from(familiesSet);
  }

  return [];
});

// Filter items to display the updated list
const filteredItems = computed(() => {
  return props.items.filter((item) => {
    const matchesIconic = iconicOnly.value ? item.iconic === true : true;
    const matchesFamily = selectedFamily.value
      ? item.serviceFamily.includes(selectedFamily.value)
      : true;
    return matchesIconic && matchesFamily;
  });
});

const centerFilteredItems = computed(() => {
  if (!selectedCenter.value) {
    return filteredItems.value;
  }
  return filteredItems.value.filter(
    (item) =>
      item.centers &&
      item.centers.some((c) => c.id_center === selectedCenter.value)
  );
});
// Map centers to label/value format for n-select
const centersName = computed(() =>
  props.centers.map((center) => ({
    label: center.title,
    value: center.id_center,
  }))
);
const journeyType = computed(() => bookingStore.journeyType);

const toggleIconicFilter = () => {
  iconicOnly.value = !iconicOnly.value;
  selectedFamily.value = "";
};
const toggleFamilyFilter = (family) => {
  selectedFamily.value = family;
  iconicOnly.value = false;
};
// Scroll Logic
// Check position to enable or disable buttons
const checkScrollPosition = () => {
  if (filtersContainer.value) {
    const { scrollLeft, scrollWidth, clientWidth } = filtersContainer.value;
    isAtStart.value = scrollLeft === 0;
    isAtEnd.value = scrollLeft + clientWidth >= scrollWidth;
  }
};
const scrollToStart = () => {
  if (filtersContainer.value) {
    filtersContainer.value.scrollBy({ left: -100, behavior: "smooth" });
  }
};
const scrollToEnd = () => {
  if (filtersContainer.value) {
    filtersContainer.value.scrollBy({ left: 100, behavior: "smooth" });
  }
};

// Check if scrollbar is needed
const updateScrollButtons = () => {
  if (filtersContainer.value) {
    const { scrollWidth, clientWidth } = filtersContainer.value;
    showScrollButtons.value = scrollWidth > clientWidth;
    checkScrollPosition();
  }
};

const selectDefaultCenter = (center) => {
  if (hasSelectedDefaultCenter.value) return;
  hasSelectedDefaultCenter.value = true;
  selectCenter(center.id_center);
  const url = new URL(window.location);
  url.searchParams.set("centerId", center.id_center);
  window.history.replaceState(null, "", url);
};
const handleSelectItem = (type, item, center) => {
  emit("selectItem", type, item, center);
};
const selectCenter = (centerId) => {
  const selectedCenterObj =
    props.centers.find((c) => c.id_center === centerId) || null;
  if (selectedCenterObj) {
    if (bookingStore.currentCenter?.id_center === selectedCenterObj.id_center) {
      return;
    }
    bookingStore.updateCurrentCenter(selectedCenterObj);
  }
  emit("selectCenter", centerId);
};

onMounted(() => {
  nextTick(() => {
    updateScrollButtons();
    window.addEventListener("resize", updateScrollButtons);
  });
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", updateScrollButtons);
});

watch(
  () => props.items,
  (newItems) => {
    localItems.value = [...newItems];
  }
);
watch(
  () => [bookingStore.currentStep, props.centers],
  ([step, centers]) => {
    if (
      step === 0 &&
      props.type === "service" &&
      !hasSelectedDefaultCenter.value &&
      centers.length === 1
    ) {
      selectDefaultCenter(centers[0]);
    }
  },
  { immediate: true, deep: true }
);
</script>

<style scoped>
.results-nb {
  display: block;
  margin-block-end: 1.875rem;
  font:
    1rem/1.3 "neue-haas-grotesk-display",
    sans-serif;
  font-weight: 600;
  letter-spacing: 0.44px;
}
.select-spa {
  margin-block-end: 1.875rem;
}
.results-nb-and-filters {
  display: flex;
  flex-direction: column;
}
:deep(.n-base-selection-placeholder__inner),
:deep(.n-base-selection .n-base-suffix .n-base-suffix__arrow) {
  color: rgb(var(--brand-black) / 1);
}
@media screen and (min-width: 1000px) {
  .results-nb {
    margin-block-end: 2rem;
    font-size: 1.25rem;
  }
  .select-spa {
    min-width: 21.5rem;
    margin-block-end: 2rem;
  }
  .results-nb-and-filters {
    justify-content: space-between;
    align-items: center;
    flex-direction: row;
  }
}
</style>
